import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { FiCalendar, FiAlertCircle, FiEye, FiEyeOff, FiClock, FiGlobe, FiCheckCircle, FiUser, FiLock, FiMail, FiX } from 'react-icons/fi';
import { SEO } from '@daycal/ui';
import { homeForRole } from '../lib/config.js';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Show error if redirected with a message
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location.state]);

  useEffect(() => {
    const cycle = setInterval(() => {
      setActiveStep((s) => (s + 1) % 4);
    }, 2000);
    return () => clearInterval(cycle);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await login(form.email, form.password);
      // Route by role: super admins land on the platform control panel, agency
      // employees on their scoped schedule, everyone else on the host dashboard.
      nav(homeForRole(user?.role));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <SEO title="Sign In - DayCal" path="/login" />
      
      <div className="grid w-full grid-cols-1 lg:grid-cols-12">
        {/* Left Side: Login Form (5 cols on lg) */}
        <div className="flex items-center justify-center px-6 py-12 sm:px-16 lg:col-span-5 bg-white relative z-10 shadow-xl lg:order-last">
          {/* Close button returning to home */}
          <Link
            to="/"
            title="Return to Home"
            className="absolute top-5 right-5 sm:top-6 sm:right-6 grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors z-20"
          >
            <FiX className="h-5 w-5" />
          </Link>

          <div className="w-full max-w-[400px] animate-dc-rise">
            
            {/* Header logo / back link */}
            <div className="mb-8 flex flex-col items-center justify-center text-center">
              <Link to="/" className="group mb-5 flex flex-col items-center">
                <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-20 w-auto -my-4 object-contain transition-transform duration-300 group-hover:scale-105" />
              </Link>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
              <p className="mt-2 text-sm text-slate-500">Sign in to manage your appointments and schedules</p>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 p-4 text-red-600 animate-pulse">
                <FiAlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0" />
                <span className="text-sm font-semibold leading-relaxed">{error}</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Email Address
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiMail className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    placeholder="admin@daycal.in"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiLock className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    required
                    placeholder="••••••••"
                    className={`w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-10 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 ${!showPassword ? 'tracking-widest' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none transition-colors"
                  >
                    {showPassword ? <FiEyeOff className="h-4.5 w-4.5" /> : <FiEye className="h-4.5 w-4.5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="mt-6 w-full rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-slate-800 active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20"
              >
                {busy ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-8 text-center text-sm font-medium text-slate-500">
              Don't have an account? <Link to="/signup" className="font-bold text-slate-900 hover:underline">Create one for free</Link>
            </div>
          </div>
        </div>

        {/* Right Side: Animated Scheduling Showcase (7 cols on lg, hidden on mobile) */}
        <div className="hidden lg:flex lg:col-span-7 bg-slate-950 text-white relative overflow-hidden items-center justify-center p-12 lg:order-first">
          {/* Decorative background grid and gradients */}
          <div className="absolute inset-0 bg-dot-grid opacity-30" />
          <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-brand-500/10 blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-indigo-500/10 blur-[100px]" />

          {/* Floating scheduling elements */}
          <div className="absolute top-12 left-16 animate-dc-float opacity-30 [animation-delay:0s]">
            <FiCalendar className="h-10 w-10 text-brand-400" />
          </div>
          <div className="absolute bottom-16 right-20 animate-dc-float opacity-30 [animation-delay:2s]">
            <FiClock className="h-12 w-12 text-indigo-400" />
          </div>
          <div className="absolute top-1/3 right-12 animate-dc-float opacity-20 [animation-delay:4s]">
            <FiGlobe className="h-8 w-8 text-emerald-400" />
          </div>

          {/* Main Visual content container */}
          <div className="relative z-10 w-full max-w-[500px]">
            <h2 className="text-3xl font-extrabold tracking-tight text-white mb-3">
              Automated Scheduling built for modern teams.
            </h2>
            <p className="text-slate-400 text-sm mb-12">
              Connect calendars, setup custom booking forms, and start accepting appointments in under two minutes.
            </p>

            {/* Simulated Booking Pipeline Widget */}
            <div className="space-y-4">
              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 0 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 0 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiClock className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">1. Select Slot</p>
                    <p className="text-xs text-slate-400 mt-0.5">Host's availability auto-converts to visitor's timezone.</p>
                  </div>
                  {activeStep > 0 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 1 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 1 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiUser className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">2. Custom Questions</p>
                    <p className="text-xs text-slate-400 mt-0.5">Collect business inputs upfront via custom booking forms.</p>
                  </div>
                  {activeStep > 1 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 2 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 2 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiCalendar className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">3. Built-in Video Calls</p>
                    <p className="text-xs text-slate-400 mt-0.5">Every online booking gets its own secure video room automatically.</p>
                  </div>
                  {activeStep > 2 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 3 
                  ? 'border-emerald-500/50 bg-slate-900 shadow-lg shadow-emerald-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 3 ? 'bg-emerald-600 text-white animate-pulse' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiCheckCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">4. Meeting Booked!</p>
                    <p className="text-xs text-slate-400 mt-0.5">Calendar invites, email confirmations & reminders sent.</p>
                  </div>
                  {activeStep === 3 && <span className="text-xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full animate-pulse">Live</span>}
                </div>
              </div>
            </div>
            
            {/* Clock Widget at bottom */}
            <div className="mt-10 flex items-center gap-4 bg-slate-900/60 border border-slate-800 p-4 rounded-2xl">
              <div className="relative h-10 w-10 rounded-full border-2 border-slate-700 flex items-center justify-center">
                {/* Clock hands */}
                <div className="absolute top-1.5 bottom-1/2 w-0.5 bg-white origin-bottom animate-dc-spin-slow" />
                <div className="absolute top-2.5 bottom-1/2 w-0.5 bg-brand-500 origin-bottom [animation-duration:5s] animate-dc-spin-slow" />
                <div className="h-1.5 w-1.5 rounded-full bg-white z-10" />
              </div>
              <div>
                <p className="text-xs font-bold">Multi-Timezone Engine Active</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Auto-matching slot windows across 24 standard UTC zones.</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
